<template>
  <div :class="['product-price', cssClasses, defaultPriceClass, authenticatedUserPriceClass, campaignPriceClass]">
    <!-- Both VAT figures per price, the chosen one first and larger (OC-3012). Which prices get the
         second figure follows the designs: a customer with agreement prices sees it only on a
         campaign price, and reference prices (ordinary, list) show it only when there is no
         agreement price. -->
    <div v-element-loader="{ endpoint: 'productListPrices', matcher: loaderMatcher, useSmallLoader: true }" class="product-price__main">
      <template v-if="!isArticle">
        <oie-product-price-group
          v-if="lowestPrice.exVat > 0"
          main
          :heading="tStatic('fromPriceLabel')"
          :price-ex-vat="lowestPrice.exVat"
          :price-inc-vat="lowestPrice.incVat"
          :show-other="!hasAgreementPrices"></oie-product-price-group>
        <oie-product-price-group
          v-if="hasAgreementPrices"
          :heading="tStatic('listPriceLabel')"
          :price-ex-vat="lowestListPrice.exVat"
          :price-inc-vat="lowestListPrice.incVat"
          :show-other="false"></oie-product-price-group>
      </template>

      <template v-else-if="hasPrice && customerPrice.exVat > 0">
        <template v-if="hasCampaignPrice">
          <oie-product-price-group
            main
            :heading="tStatic('campaignPrice')"
            :price-ex-vat="campaignPrice.exVat"
            :price-inc-vat="campaignPrice.incVat"></oie-product-price-group>
          <oie-product-price-group
            :heading="tStatic('ordinaryPriceLabel')"
            :price-ex-vat="customerPrice.exVat"
            :price-inc-vat="customerPrice.incVat"
            :show-other="!hasAgreementPrice"></oie-product-price-group>
        </template>

        <oie-product-price-group
          v-else
          main
          :price-ex-vat="customerPrice.exVat"
          :price-inc-vat="customerPrice.incVat"
          :show-other="!hasAgreementPrice"></oie-product-price-group>

        <oie-product-price-group
          v-if="hasAgreementPrice"
          :heading="tStatic('listPriceLabel')"
          :price-ex-vat="listPrice.exVat"
          :price-inc-vat="listPrice.incVat"
          :show-other="false"></oie-product-price-group>
      </template>
    </div>

    <oie-product-bracket-prices
      v-if="showBracketPrices"
      class="product-price__bracket-prices"
      :bracket-prices="bracketPrices"></oie-product-bracket-prices>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

// A price both ways. Falls back to the single switch-following figure when the response has no
// separate ones, which then shows as one figure with no second line.
function pricePair(exVat, incVat, legacy) {
  if (exVat === undefined || exVat === null || incVat === undefined || incVat === null) {
    const value = legacy === undefined || legacy === null || legacy === '' ? 0 : Number(legacy)
    return { exVat: value, incVat: value }
  }
  return { exVat: Number(exVat), incVat: Number(incVat) }
}

function differs(a, b) {
  return Math.abs((a || 0) - (b || 0)) >= 0.005
}

export default {
  props: {
    currentObject: {
      type: Object,
      required: true
    },
    isProduct: {
      type: Boolean,
      required: true
    },
    showBracketPrices: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapGetters('ui', ['useVat']),
    currentObjectItemCount() {
      return _.get(this, 'currentObject.items.length')
    },
    current() {
      if(this.isProduct) {
        if(this.currentObjectItemCount === 1) {
          return this.currentObject.items[0]
        }
      }
      return this.currentObject
    },
    hasPrice() {
      return this.current.customerPrice !== null && this.current.customerPrice !== undefined
    },
    priceDto() {
      return this.hasPrice ? this.current.customerPrice : {}
    },
    lowestPrice() {
      if(!this.isProduct) {
        return { exVat: 0, incVat: 0 }
      }
      return pricePair(this.current.lowestPriceExVat, this.current.lowestPriceIncVat, this.current.lowestPrice)
    },
    lowestListPrice() {
      return pricePair(this.current.lowestListPriceExVat, this.current.lowestListPriceIncVat, null)
    },
    customerPrice() {
      const dto = this.priceDto
      const legacy = dto.customerPrice !== undefined ? dto.customerPrice : dto.listPrice
      return pricePair(
        dto.customerPriceExVat !== undefined ? dto.customerPriceExVat : dto.listPriceExVat,
        dto.customerPriceIncVat !== undefined ? dto.customerPriceIncVat : dto.listPriceIncVat,
        legacy)
    },
    hasCampaignPrice() {
      return this.hasPrice && this.priceDto.campaignPrice !== undefined
    },
    campaignPrice() {
      return pricePair(this.priceDto.campaignPriceExVat, this.priceDto.campaignPriceIncVat, this.priceDto.campaignPrice)
    },
    listPrice() {
      return pricePair(this.priceDto.listPriceExVat, this.priceDto.listPriceIncVat, this.priceDto.listPrice)
    },
    countItems() {
      if(this.isProduct) {
        return this.currentObjectItemCount
      }

      return 1
    },
    // The customer pays an agreement price of their own rather than the list price.
    hasAgreementPrice() {
      return this.hasPrice && !this.isProductOwner && differs(this.listPrice.exVat, this.customerPrice.exVat)
    },
    // The same for a product with several variants, judged on the lowest prices. Only a signed-in
    // customer can have agreement prices, which keeps a campaign on one variant from looking like one.
    hasAgreementPrices() {
      return this.isAuthenticated && differs(this.lowestListPrice.exVat, this.lowestPrice.exVat)
    },
    isArticle() {
      return this.countItems === 1
    },
    bracketPrices() {
      return this.current.customerPrice
        ? this.current.customerPrice.bracketPrices
        : []
    },
    isAuthenticated() {
      return this.clientModel.isAuthenticated
    },
    defaultPriceClass() {
      return !this.isAuthenticated && !this.hasCampaignPrice && 'product-price--default'
    },
    authenticatedUserPriceClass() {
      return this.isAuthenticated && !this.hasCampaignPrice && 'product-price--authenticated-user'
    },
    campaignPriceClass() {
      return this.hasCampaignPrice && 'product-price--campaign'
    }
  },
  methods: {
    loaderMatcher(data) {
      return _.chain(data).get('productId', []).includes(_.get(this, 'currentObject.id')).value()
    }
  }
}
</script>

<style lang="scss">
.product-price {
  @include caption;
}

.product-price--default {
  color: color(text-brand-blue);
}

.product-price--authenticated-user {
  color: color(text-blue);
}

.product-price--campaign {
  color: color(text-red);
}

.product-price__top {
  height: 0.8125rem;
  margin-bottom: measure(eighth);
}

.product-price__text {
  @include caption-semi-bold;
  margin-bottom: measure(eighth);
}

.product-price__text--campaign {
  color: color(text-red);
  margin-bottom: 0;
}

.product-price__text--blue-plate {
  display: inline-block;
  padding: measure(eighth) measure(quarter);
  background-color: color(blue-5);
  color: color(text-brand-blue);
  border-radius: 0.1875rem;
}

.product-price__text--vat {
  @include caption;
  margin-bottom: 0;
}

.product-price__main {
  min-height: 1.4rem;
}

.product-price .product-price-group:not(.product-price-group--main) {
  color: color(text-brand-blue);
}

.product-price__reduced {
  @include caption;
  text-decoration: line-through;
  color: color(text)
}

.product-price__bracket-prices {
  margin-top: measure(quarter);
}
</style>
